'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { logos, testimonialImages } from '../assets/images';
import { useSecureAuthContext } from '@/core/auth/services';
import { Poppins } from 'next/font/google';
import LogoLoop from '../effects/LogoLoop';
import CountUp from '@/shared/components/CountUp';
import { Users, Zap, RefreshCw, ChevronRight, ShieldCheck, ExternalLink } from 'lucide-react';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

export default function ExternalHomePage() {
  const router = useRouter();
  const { isAuthenticated, isLoading } = useSecureAuthContext();
  const whyFeatureCarouselRef = useRef<HTMLDivElement | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<'collaboration' | 'respond' | 'sync'>('collaboration');
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});

  const handleFeatureSelect = (feature: 'collaboration' | 'respond' | 'sync') => {
    Object.entries(videoRefs.current).forEach(([_key, video]) => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
    setSelectedFeature(feature);
    const selectedVideo = videoRefs.current[feature];
    if (selectedVideo) {
      selectedVideo.play().catch(() => {});
    }
  };

  const featureContent = {
    collaboration: {
      title: 'Work together seamlessly across your entire agency.',
      description: 'Assign co-workers into conversations, hand off clients with full context, and keep everyone aligned with shared notes and internal team chat.',
      video: '/images/why/teamCollaborationvid.mp4',
    },
    respond: {
      title: 'Respond to customers faster than ever before.',
      description: 'Smart routing, instant notifications, and suggested replies help your team respond in seconds — not hours.',
      video: '/images/why/respondFastervid.mp4',
    },
    sync: {
      title: 'Every conversation automatically logged to your AMS.',
      description: 'Deep HawkSoft integration means every call and text syncs to the right client record automatically.',
      video: '/images/why/AMSvid.mp4',
    },
  };
  
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace('/dashboard');
    }
  }, [isAuthenticated, isLoading, router]);

  // Don't render the page if user is authenticated (being redirected)
  if (isAuthenticated) {
    return null;
  }

  const scrollWhyFeatureCarousel = (direction: 'left' | 'right') => {
    const el = whyFeatureCarouselRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };
  
  return (
    <div className="bg-white">
      {/* Partnership Announcement Banner */}
      <div
        className="relative overflow-hidden -mt-4 sm:-mt-6"
        style={{
          backgroundImage: `linear-gradient(135deg, ${BRAND_BLUE}12 0%, ${BRAND_GREEN}18 50%, ${BRAND_BLUE}08 100%)`,
        }}
      >
        <div
          className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 py-2.5 sm:py-3"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          <a
            href="https://www.prnewswire.com/news-releases/hawksoft-and-voley-announce-api-partnership-to-promote-automated-communications-302724043.html"
            target="_blank"
            rel="noreferrer noopener"
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 group"
          >
            {/* Partnership badge */}
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs sm:text-sm font-semibold tracking-wide uppercase whitespace-nowrap"
              style={{ backgroundColor: `${BRAND_GREEN}30`, color: '#166534' }}
            >
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: BRAND_GREEN }} />
              Official Partnership
            </span>

            {/* Logos side by side */}
            <span className="flex items-center gap-3">
              <Image src={logos.hawkSoft} alt="HawkSoft" height={28} className="h-6 sm:h-7 w-auto object-contain" />
              <span className="text-gray-400 text-lg font-light select-none">&times;</span>
              <Image src={logos.voleyT} alt="Voley" height={22} className="h-5 sm:h-[22px] w-auto object-contain" />
            </span>

            {/* Announcement text */}
            <span className="text-sm sm:text-base text-gray-700 text-center sm:text-left">
              HawkSoft and Voley announce API partnership for automated agency communications
            </span>

            {/* Read more link */}
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap transition-colors duration-200 group-hover:underline" style={{ color: BRAND_BLUE }}>
              Read the press release
              <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </a>
        </div>

        {/* Subtle bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ backgroundImage: `linear-gradient(90deg, transparent 0%, ${BRAND_GREEN}40 50%, transparent 100%)` }} />
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden min-h-[80vh]">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 pt-6 pb-4 sm:pt-8 sm:pb-6 relative z-10" style={{ maxWidth: 'min(1920px, 100vw)' }}>
          <div className="text-center flex flex-col items-center pt-2 pb-4">
            <div className="max-w-7xl animate-float-up-once">
              {/* Badge */}
              <div className="flex flex-col items-center mb-4">
                <p className="text-xs sm:text-base md:text-lg font-medium text-gray-500 tracking-wide">
                  Innovative Insuretech 2025 <span className="text-gray-400 mx-2">|</span> 4.9 <span className="text-gray-500">★</span>
                </p>
                <div className="w-48 sm:w-64 md:w-72 lg:w-80 h-px bg-gray-300 mt-3" />
              </div>
              {/* Title */}
              <h1 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight mt-4 sm:mt-6`}>
                <span className="block font-medium" style={{ color: '#111827' }}>
                  The Future of
                </span>
                <span className="block font-medium" style={{ color: '#111827' }}>
                  Insurance Communication
                </span>
              </h1>
              <p className="mt-4 sm:mt-5 text-base sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl text-gray-500 font-body font-light max-w-4xl mx-auto text-center leading-relaxed px-4">
                Built on deep AMS integration, designed for agency-wide collaboration, and evolving with the latest messaging innovation,
                <br />
                <span className="font-semibold text-gray-700">
                  Voley is the communication infrastructure built for insurance agencies.
                </span>
              </p>
              {/* Three dots */}
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_BLUE }} />
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BRAND_YELLOW }} />
              </div>
              {/* CTA Buttons */}
              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  href="/request-demo"
                  className="whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto sm:min-w-[180px] px-5 py-2 border border-[#BAE3C2] rounded-md text-base sm:text-lg font-medium text-gray-900 bg-[#BAE3C2] shadow-sm transition-all duration-200 hover:bg-brand/90 hover:text-white hover:border-brand/90 hover:shadow-md hover:scale-[1.01] hover:-translate-y-px"
                >
                  Request a Demo
                </Link>
                <Link
                  href="/get-started"
                  className="whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto sm:min-w-[180px] px-5 py-2 border border-gray-200 rounded-md text-base sm:text-lg font-medium text-gray-900 bg-white shadow-sm transition-all duration-200 hover:bg-brand/90 hover:text-white hover:border-brand/90 hover:shadow-md hover:scale-[1.01] hover:-translate-y-px"
                >
                  Try for Free
                </Link>
              </div>
            </div>

            {/* Hero video inside gradient frame - outside max-w-7xl for full width */}
            <div className="mt-8 sm:mt-12 md:mt-16 w-full flex justify-center px-2 sm:px-0">
                <div
                  className="w-full aspect-[2584/1682] rounded-xl p-2 sm:p-4 md:p-6 lg:p-8 flex items-center justify-center"
                  style={{
                    maxWidth: 'min(1800px, calc(100vw - 32px))',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(53, 129, 207, 0.8), rgba(134, 208, 157, 0.6), rgba(243, 221, 96, 0.6))',
                  }}
                >
                  <div className="w-[96%] h-[96%] rounded-lg overflow-hidden flex items-center justify-center relative" style={{ backgroundColor: '#F8F9FB' }}>
                    {(['collaboration', 'respond', 'sync'] as const).map((feature) => (
                      <div
                        key={feature}
                        className={`absolute inset-0 w-full h-full flex items-center justify-center transition-opacity duration-150 ${
                          selectedFeature === feature ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                      >
                        <video
                          ref={(el) => { videoRefs.current[feature] = el; }}
                          src={featureContent[feature].video}
                          className="w-full object-contain bg-[#F8F9FB]"
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload={selectedFeature === feature ? 'auto' : 'metadata'}
                          onCanPlay={(e) => e.currentTarget.play()}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>

      {/* Feature Pills */}
      <div className="bg-white mt-4 sm:mt-6 md:mt-8 pb-6 sm:pb-8 relative z-20">
        <div
          className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          <div className="flex flex-wrap justify-center items-center gap-0 mb-4 sm:mb-6">
            <button
              type="button"
              onClick={() => handleFeatureSelect('collaboration')}
              className={`inline-flex items-center gap-2 sm:gap-3 rounded-md px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-base sm:text-lg md:text-xl lg:text-2xl font-medium transition-all duration-200 cursor-pointer ${
                selectedFeature === 'collaboration'
                  ? 'text-gray-900 bg-brand-yellow/80'
                  : 'text-gray-900 hover:bg-brand-yellow/80'
              }`}
            >
              <Users className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" /> <span className="whitespace-nowrap">Team Collaboration</span>
            </button>
            <div className="w-px h-6 sm:h-8 bg-gray-300 mx-2 sm:mx-4" />
            <button
              type="button"
              onClick={() => handleFeatureSelect('respond')}
              className={`inline-flex items-center gap-2 sm:gap-3 rounded-md px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-base sm:text-lg md:text-xl lg:text-2xl font-medium transition-all duration-200 cursor-pointer ${
                selectedFeature === 'respond'
                  ? 'text-gray-900 bg-brand-yellow/80'
                  : 'text-gray-900 hover:bg-brand-yellow/80'
              }`}
            >
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" /> <span className="whitespace-nowrap">Respond Faster</span>
            </button>
            <div className="w-px h-6 sm:h-8 bg-gray-300 mx-2 sm:mx-4" />
            <button
              type="button"
              onClick={() => handleFeatureSelect('sync')}
              className={`inline-flex items-center gap-2 sm:gap-3 rounded-md px-4 sm:px-6 md:px-8 py-2 sm:py-3 text-base sm:text-lg md:text-xl lg:text-2xl font-medium transition-all duration-200 cursor-pointer ${
                selectedFeature === 'sync'
                  ? 'text-gray-900 bg-brand-yellow/80'
                  : 'text-gray-900 hover:bg-brand-yellow/80'
              }`}
            >
              <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" /> <span className="whitespace-nowrap">AMS Sync & Log</span>
            </button>
          </div>
          
          {/* Dynamic Text - fixed height container */}
          <div className="text-center max-w-5xl mx-auto min-h-[160px] sm:min-h-[140px] md:min-h-[120px] flex items-start justify-center px-2">
            <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gray-900 leading-relaxed">
              <span className="font-semibold">{featureContent[selectedFeature].title}</span>{' '}
              <span className="text-gray-500">{featureContent[selectedFeature].description}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Why Choose Voley Section */}
      <div className="py-8 lg:py-12 xl:py-14 bg-white">
            <div 
          className="mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-20"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          {/* Section intro for feature cards */}
          <div className="mt-0 flex flex-col sm:flex-row sm:items-end">
            <div className="text-left flex-1 sm:mr-8">
              <h2 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}>
                <span className="block font-medium" style={{ color: '#111827' }}>
                  Why Choose Voley?
                </span>
              </h2>
              <p className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-relaxed font-normal text-gray-600 max-w-4xl">
              Voley is built specifically for insurance agencies to make every client conversation centralized, supervised, and defensible — <span className="font-bold">automatically.</span>
              </p>
            </div>
            {/* Arrow buttons */}
            <div className="hidden md:flex items-center gap-3 flex-shrink-0 mr-0 lg:mr-12 mt-4 sm:mt-0">
              <button
                type="button"
                onClick={() => scrollWhyFeatureCarousel('left')}
                className="h-10 w-10 md:h-14 md:w-14 flex items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                aria-label="Scroll features left"
              >
                <span className="text-gray-600 text-2xl md:text-3xl font-light">{'‹'}</span>
              </button>
              <button
                type="button"
                onClick={() => scrollWhyFeatureCarousel('right')}
                className="h-10 w-10 md:h-14 md:w-14 flex items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all duration-200"
                aria-label="Scroll features right"
              >
                <span className="text-gray-600 text-2xl md:text-3xl font-light">{'›'}</span>
              </button>
            </div>
          </div>

          {/* Feature cards carousel */}
          <div className="mt-10 relative">
            {/* Carousel track */}
            <div ref={whyFeatureCarouselRef} className="overflow-hidden pb-2 scroll-smooth">
              <div className="flex gap-6 lg:gap-8 min-w-max">
                {[
                  {
                    title: 'Automated Compliance',
                    displayLine1: 'Protect your',
                    displayLine2: 'team',
                    subtext: 'Automated compliance and built-in guardrails for your agency.',
                  },
                  {
                    title: 'Deep Integrations',
                    displayLine1: 'Never miss',
                    displayLine2: 'a lead',
                    subtext: 'Communication across all systems put into one inbox.',
                  },
                  {
                    title: 'Centralized Communication Hub',
                    displayLine1: 'Save hundreds',
                    displayLine2: 'of hours',
                    subtext: 'Deep AMS integrations, templates, and other enhancement features.',
                  },
                  {
                    title: 'Collaborative Design',
                    displayLine1: 'Work smarter',
                    displayLine2: 'together',
                    subtext: 'Collaborative design, internal messages, assignments, and more.',
                  },
                  {
                    title: 'Smart Insurance Tools',
                    displayLine1: 'Never fall',
                    displayLine2: 'behind',
                    subtext: 'Be at the forefront of communication innovation.',
                  },
                ].map((feature, index, arr) => (
                  <div key={feature.title} className="flex shrink-0">
                    <a
                      href="#why-apart"
                      className="w-[260px] sm:w-[300px] md:w-[340px] px-6 py-5 sm:px-7 sm:py-6 flex flex-col justify-center group"
                    >
                      <h3 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-snug bg-gradient-to-r from-brand via-brand-green to-brand-yellow bg-clip-text text-transparent group-hover:text-gray-900 group-hover:bg-none">
                        {feature.displayLine1}
                        {feature.displayLine2 && <><br />{feature.displayLine2}</>}
                      </h3>
                      <p className="mt-1 text-lg sm:text-xl md:text-2xl font-medium text-gray-600 leading-snug group-hover:text-gray-700">
                        {feature.subtext}
                      </p>
                    </a>
                    {index < arr.length - 1 && (
                      <div className="w-px bg-gray-300 self-stretch mx-2" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Modern communication layer text + image */}
          <div className="mt-4 sm:mt-6 md:mt-8 flex flex-col lg:flex-row items-start lg:items-center lg:justify-between gap-8 lg:gap-12">
            <div className="flex-1 lg:order-1 flex justify-center lg:justify-start w-full">
              <div className="w-full max-w-[400px] sm:max-w-[500px] lg:max-w-[640px]">
                <img
                  src="/images/why/layer.png"
                  alt="Layered communication illustration"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div className="flex-1 max-w-2xl lg:order-2">
              <h2
                className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl ${poppins.className} tracking-tight leading-tight text-left`}
              >
                <span className="block font-medium" style={{ color: '#111827' }}>
                Everything your agency needs to communicate, collaborate, and grow
                </span>
              </h2>
              <p className="mt-3 sm:mt-4 text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl text-gray-500 font-body font-light max-w-3xl text-left leading-relaxed">
              A complete communications platform combining voice, SMS, rich messaging, and team collaboration—so agencies can manage every conversation in one place.
              </p>
              <div className="mt-4 sm:mt-6 flex flex-wrap gap-2 sm:gap-3">
                {[
                  { label: 'VOIP' },
                  { label: 'SMS' },
                  { label: 'Team Chat' },
                  { label: 'Mobile App' },
                ].map(item => (
                  <a
                    key={item.label}
                    href="/solutions#powerful-features"
                    className="inline-flex items-center gap-1 rounded-full px-4 sm:px-5 py-1.5 sm:py-2 text-sm sm:text-base md:text-lg font-medium text-gray-900 hover:shadow-md transition-shadow duration-200"
                    style={{ backgroundColor: 'rgba(243, 221, 96, 0.8)' }}
                  >
                    {item.label}
                    <ChevronRight size={14} className="sm:w-4 sm:h-4 text-gray-600" />
                  </a>
                ))}
              </div>
            </div>
          </div>

                      </div>
                    </div>

      {/* Testimonial highlight section */}
      <div className="bg-white py-10 sm:py-12 md:py-14 lg:py-20">
        <div
          className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          <div className="rounded-2xl sm:rounded-3xl lg:rounded-[2.75rem] bg-gray-50 overflow-hidden flex flex-col lg:flex-row shadow-sm">
            {/* Left gradient stat card */}
            <div className="relative lg:w-5/12">
              <div
                className="h-[200px] sm:h-[260px] md:h-[320px] lg:h-full w-full"
                style={{
                  backgroundImage:
                    `linear-gradient(135deg, #e6f9f0 0%, #c8f1dd 25%, #a4e3c3 50%, ${BRAND_GREEN} 75%, #4fa871 100%)`,
                }}
              />
              <div className="pointer-events-none absolute inset-0 flex flex-col justify-end px-6 sm:px-8 md:px-10 py-6 sm:py-8 md:py-10 text-white">
                <div>
                  <p className="text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] 2xl:text-[14rem] font-semibold tracking-tight">
                    <CountUp from={0} to={3000} separator="," duration={2} />
                  </p>
                  <p className="mt-2 sm:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white/80">hours saved monthly</p>
                </div>
                      </div>
                    </div>

            {/* Right testimonial content */}
            <div className="flex-1 bg-[#f7f5f2] px-6 sm:px-8 md:px-10 lg:px-14 py-8 sm:py-10 lg:py-12 flex flex-col justify-center">
              <p className="text-4xl sm:text-5xl md:text-6xl text-black leading-none mb-3 sm:mb-4 select-none">
                &ldquo;
              </p>
              <p className="text-xs font-semibold tracking-[0.22em] text-gray-500 uppercase mb-3 sm:mb-4">
                CUSTOMER STORY
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-snug mb-3 sm:mb-4">
                Voley became the communication hub our agency needed.
              </p>
              <p className="text-lg sm:text-xl md:text-2xl lg:text-2xl text-gray-600 leading-relaxed mb-6 sm:mb-8">
                Our phones, texting, and internal chat finally live in one place. The HawkSoft integration changed everything—every call and text automatically logs to the right client record, so nothing slips through the cracks. Response times are faster and our team actually enjoys the tools they use every day.
              </p>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full overflow-hidden bg-gray-300">
                  <img
                    src="/testimonial_profiles/keystone_testimonial.jpeg"
                    alt="Portrait of Brent Thurman"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">Brent Thurman</p>
                  <p className="text-base sm:text-lg text-gray-500">Owner, Keystone Insurance</p>
                </div>
              </div>
                    </div>
                      </div>

          {/* Additional gradient stat cards row */}
          <div className="mt-6 sm:mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Stat card 1 */}
            <div className="rounded-2xl sm:rounded-[2.25rem] overflow-hidden shadow-sm">
              <div
                className="relative h-[220px] sm:h-[280px] md:h-[340px] w-full flex flex-col justify-end px-6 sm:px-8 md:px-10 pb-6 sm:pb-8 md:pb-12 text-white"
                style={{
                  backgroundImage:
                    `linear-gradient(135deg, ${BRAND_BLUE} 0%, #4a9ad8 30%, #6ab3c5 55%, ${BRAND_GREEN} 80%, #e6f9f0 100%)`,
                }}
              >
                <p className="text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] xl:text-[12rem] font-semibold tracking-tight">
                  <CountUp from={0} to={4} suffix="x" duration={2} />
                </p>
                <p className="mt-2 sm:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white/80">
                  smoother workflows
                </p>
              </div>
            </div>
            {/* Stat card 2 */}
            <div className="rounded-2xl sm:rounded-[2.25rem] overflow-hidden shadow-sm">
              <div
                className="relative h-[220px] sm:h-[280px] md:h-[340px] w-full flex flex-col justify-end px-6 sm:px-8 md:px-10 pb-6 sm:pb-8 md:pb-12 text-white"
                style={{
                  backgroundImage:
                    `linear-gradient(135deg, ${BRAND_BLUE} 0%, #4a9ad8 30%, #6ab3c5 55%, ${BRAND_GREEN} 80%, #e6f9f0 100%)`,
                }}
              >
                <p className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[10rem] font-semibold tracking-tight">
                  <CountUp from={0} to={230000} suffix="+" duration={2} separator="," />
                </p>
                <p className="mt-2 sm:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white/80">
                  potential E&O risks identified & flagged
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Voley deep-dive */}
      <div id="why-apart" className="bg-white py-12 sm:py-14 md:py-16 lg:py-20">
        <div
          className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          <div className="max-w-5xl">
            <h2
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
            >
              <span className="block font-medium" style={{ color: '#111827' }}>
                What sets Voley apart.
              </span>
            </h2>
            <p className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gray-600 font-body leading-relaxed">
              Beyond channels and features, Voley is designed around how insurance teams actually work — from compliance
              and integrations to collaboration and insight.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              {
                title: 'Be on the channel clients prefer',
                subtitle: 'Meet them where they are',
                body: 'The next generation of clients prefer to text. Stop putting them on hold and let them reach you the way they want — through calls, texts, or both.',
              },
              {
                title: 'Talk to more people in less time',
                subtitle: 'Scale with ease',
                body: 'Handle multiple conversations at once without dropping the ball. Your team gets more done in less time, so they can focus on what matters — building relationships.',
              },
              {
                title: 'Skip the data entry',
                subtitle: 'Auto-log everything',
                body: 'Every conversation syncs to your AMS automatically. Your records stay complete, compliance stays tight, and your team saves hours every week.',
              },
              {
                title: 'Never lose context again',
                subtitle: 'One timeline per client',
                body: 'Every call, text, and note in one place. When a client reaches out, your team has the full picture instantly — no hunting through systems.',
              },
              {
                title: 'Protect you and your agency',
                subtitle: 'E&O guardrails',
                body: 'Built-in compliance tools help flag risky messages and keep your team aligned with best practices — reducing E&O exposure before it becomes a problem.',
              },
              {
                title: 'Work together seamlessly',
                subtitle: 'Built for teams',
                body: 'Hand off clients with full context, tag teammates into conversations, and keep everyone aligned — so nothing falls through the cracks.',
              },
              {
                title: 'Stay compliant from day one',
                subtitle: '10DLC & federal compliance',
                body: 'We handle carrier registration, 10DLC compliance, and federal messaging regulations so you can focus on your clients — not paperwork.',
              },
              {
                title: 'The most comprehensive audit trails',
                subtitle: 'Complete visibility',
                body: 'Every message, every action, fully logged and searchable. Our audit trails go deeper than any competitor — giving you complete peace of mind for compliance and accountability.',
              },
            ].map(feature => (
              <div key={feature.title} className="flex items-start gap-4">
                <div className="mt-1 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand shadow-sm">
                  <span className="text-white text-xl font-semibold leading-none">✓</span>
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 leading-snug">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-lg sm:text-xl md:text-2xl text-gray-500 leading-relaxed">
                    {feature.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* See details link */}
          <div className="mt-8 sm:mt-10 flex justify-center sm:justify-end sm:mr-12">
            <Link
              href="/solutions#powerful-features"
              className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg md:text-xl font-medium text-brand bg-blue-50 hover:bg-blue-100 rounded-full transition-all"
            >
              See more details
              <svg className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>

          {/* E&O Smart Tracking highlight card */}
          <div className="mt-10 sm:mt-12 md:mt-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-4 sm:mb-6 text-center px-2">
              We know insurance better than anybody
            </h3>
            <div className="relative rounded-2xl sm:rounded-[2rem] overflow-hidden px-4 py-10 sm:px-8 sm:py-12 md:px-10 md:py-14 lg:px-14 lg:py-20 bg-gray-800">
              <div className="relative z-10 text-center">
                <h4 className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-[11rem] 2xl:text-[13rem] ${poppins.className} font-semibold leading-tight tracking-tight flex items-center justify-center gap-3 sm:gap-4 md:gap-6`}>
                  <ShieldCheck className="hidden sm:inline-block w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
                  <span>
                    <span className="text-white">New </span>
                    <span className="uppercase bg-gradient-to-r from-brand via-brand-green to-brand-yellow bg-clip-text text-transparent">E&O smart-tracking</span>
                    <span className="text-white"> technology</span>
                  </span>
                  <ShieldCheck className="hidden sm:inline-block w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white" />
                </h4>
                <p className="mt-4 sm:mt-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/90 font-light max-w-3xl mx-auto px-2">
                  Built in guardrails to protect and train your agency
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Customer-driven section */}
      <div id="capabilities-top" className="bg-white pt-6 sm:pt-8 md:pt-10 lg:pt-12 pb-12 sm:pb-14 md:pb-16 lg:pb-20">
        <div
          className="mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20"
          style={{ maxWidth: 'min(1600px, 94vw)' }}
        >
          <div className="text-center max-w-5xl mx-auto">
            <h2
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl ${poppins.className} tracking-tight leading-tight`}
            >
              <span className="block font-medium" style={{ color: '#111827' }}>
                Built with agencies, not just for them.
              </span>
            </h2>
            <p className="mt-3 sm:mt-4 text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-600 font-body leading-relaxed max-w-3xl mx-auto px-2">
              Founded by real agency owners, every feature and improvement comes from real conversations with agencies like yours.
              We listen, we build, and we keep getting better together.
            </p>
          </div>

          <div className="mt-10">
            <div className="rounded-[2.5rem] bg-white border border-gray-100 shadow-sm overflow-hidden lg:flex lg:items-stretch">
              {/* Left image: awards illustration */}
              <div className="w-full lg:w-2/5 min-h-[300px] lg:min-h-0">
                <img
                  src="/images/why/awards2.png"
                  alt="Awards and recognition for modern agency communication"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Right testimonials - 2x2 grid */}
              <div className="w-full lg:w-3/5 p-6 sm:p-8 lg:p-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 h-full">
                  {[
                    {
                      name: 'Jerris Koplin',
                      position: 'Do Right Insurance',
                      quote: 'We have noticed our clients asking more and more about being able to communicate through text. Glad we made the switch over, Voley has helped us see the potential for texting in our agency.',
                      image: testimonialImages.doRight,
                      bgColor: 'rgba(53, 129, 207, 0.12)', // blue
                    },
                    {
                      name: 'Sarah Beasley',
                      position: 'Metro Insurance Group',
                      quote: 'Having everything connected to our AMS means less jumping between platforms. The simplicity has made a real difference.',
                      image: testimonialImages.metro,
                      bgColor: 'rgba(134, 208, 157, 0.2)', // green
                    },
                    {
                      name: 'David Choi',
                      position: 'Pacific Shield Insurance',
                      quote: 'Getting my agency used to it took maybe two days. I was ready to troubleshoot for weeks but honestly it just worked.',
                      image: testimonialImages.pacificshield,
                      bgColor: 'rgba(243, 221, 96, 0.2)', // yellow
                    },
                    {
                      name: 'Robert Williamson',
                      position: 'Homestead Insurance',
                      quote: 'We\'ve eliminated three separate software subscriptions we used to need. Thank you Voley!',
                      image: testimonialImages.homestead,
                      bgColor: 'rgba(180, 130, 200, 0.15)', // soft purple
                    },
                  ].map((testimonial) => (
                    <div
                      key={testimonial.name}
                      className="rounded-2xl p-6 flex flex-col bg-white hover:shadow-md transition-shadow h-full"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-200 shrink-0">
                          <Image
                            src={testimonial.image}
                            alt={`Photo of ${testimonial.name}`}
                            width={48}
                            height={48}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div>
                          <p className="text-base sm:text-lg font-semibold text-gray-900">{testimonial.name}</p>
                          <p className="text-sm sm:text-base text-gray-600">{testimonial.position}</p>
                        </div>
                      </div>
                      <p className="mt-4 text-base sm:text-lg md:text-xl text-gray-800 leading-relaxed font-medium flex-1">
                        &ldquo;{testimonial.quote}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Try us out section */}
          <div className="mt-12 sm:mt-16 md:mt-20 lg:mt-24 text-center">
            <h2 className={`text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl 2xl:text-[10rem] ${poppins.className} tracking-tight leading-tight`}>
              <span className="font-medium" style={{ color: '#111827' }}>Curious? </span>
              <span className="font-medium" style={{ color: BRAND_BLUE }}>Try 30 days free</span>
            </h2>
            <div className="mt-8 sm:mt-10 md:mt-12">
              <a
                href="/get-started"
                className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.01] hover:-translate-y-px whitespace-nowrap"
              >
                Get started
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Roadmap Section */}
      <div className="bg-white pt-2 lg:pt-3 xl:pt-4 pb-10 sm:pb-12 md:pb-14 lg:pb-16">
        <div className="mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-20" style={{ maxWidth: 'min(1600px, 94vw)' }}>
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            
            {/* Left Content - Official partner spotlight, integrations carousel & CTA */}
            <div className="lg:col-span-5 mb-12 lg:mb-0">
              <div className="space-y-6">
                {/* HawkSoft Partnership Spotlight Card */}
                <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  {/* Card header */}
                  <div className="px-5 py-3 flex items-center gap-3" style={{ backgroundColor: `${BRAND_GREEN}12` }}>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
                      style={{ backgroundColor: `${BRAND_GREEN}25`, color: '#166534' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                      Official Partner
                    </span>
                    <Image src={logos.hawkSoft} alt="HawkSoft" height={24} className="h-6 w-auto object-contain" />
                  </div>

                  {/* Quote */}
                  <div className="px-5 py-4">
                    <p className="text-base sm:text-lg text-gray-700 leading-relaxed italic">
                      &ldquo;Independent agencies are drowning in disconnected tools, and every gap between systems could be a gap in service, compliance, and accountability.&rdquo;
                    </p>
                    <p className="mt-3 text-sm font-semibold text-gray-900">Rushang Shah</p>
                    <p className="text-sm text-gray-500">Chief Marketing Officer, HawkSoft</p>
                    <a
                      href="https://www.prnewswire.com/news-releases/hawksoft-and-voley-announce-api-partnership-to-promote-automated-communications-302724043.html"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 hover:underline"
                      style={{ color: BRAND_BLUE }}
                    >
                      Read the announcement
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>

                {/* Integration partners carousel */}
                <div className="w-full">
                  <div
                    className="mx-auto"
                    style={{
                      height: '64px',
                      maxWidth: 'min(900px, 92vw)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <LogoLoop
                      logos={[
                        { node: <Image src={logos.hawkSoft} alt="HawkSoft" height={40} className="h-10 w-auto object-contain" /> },
                        { node: <Image src={logos.agencyZoom} alt="AgencyZoom" height={40} className="h-10 w-auto object-contain" /> },
                        { node: <Image src={logos.appliedEpic} alt="Applied Epic" height={40} className="h-10 w-auto object-contain" /> },
                      ]}
                      speed={80}
                      direction="left"
                      logoHeight={40}
                      gap={56}
                      fadeOut
                      fadeOutColor="#ffffff"
                      ariaLabel="Integration partners"
                    />
                  </div>
                </div>

                <div className="text-center lg:text-left">
                  <p className="text-lg sm:text-xl md:text-2xl text-gray-600 font-body leading-relaxed mb-6">
                    Our mission is to make Voley the communication hub for every insurance agency — no matter what tools you use. Want your platform supported next?
                  </p>

                  <div className="flex flex-col items-center lg:items-start space-y-3">
                    <Link
                      href="/request-integration"
                      className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-base sm:text-lg font-semibold rounded-full text-white shadow-md transition-all duration-200 hover:shadow-lg hover:scale-[1.01] hover:-translate-y-px"
                      style={{ backgroundColor: BRAND_BLUE }}
                    >
                      Request Your Integration
                    </Link>
                    <p className="text-base sm:text-lg text-gray-500 font-body">
                      Add your AMS/CRM to our roadmap
                    </p>
                    <p className="mt-4 text-xs text-gray-400">
                      HawkSoft is an official integration partner. Other logos represent planned integrations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Content - Main Headlines */}
            <div className="lg:col-span-7">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-tight"
                style={{ color: '#111827' }}
              >
                We&apos;re just getting started.
              </h2>

              <p className="mt-4 text-xl sm:text-2xl md:text-3xl text-gray-600 font-body font-light leading-relaxed max-w-2xl">
                Voley already connects with systems like HawkSoft — and we&apos;re actively expanding. More AMS/CRM
                integrations, smarter workflows, and AI-powered assistance are on the way.
              </p>

              <div className="mt-8 space-y-4">
                <p className="text-base sm:text-lg font-semibold text-gray-700 font-heading uppercase tracking-[0.16em]">
                  Coming soon to the Voley platform
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    'AI-powered workflows',
                    'Smart reply suggestions',
                    'Insurance-trained AI call receptionist',
                    'Advanced analytics',
                    'Deeper AMS + CRM integrations',
                  ].map(label => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-full bg-white/80 text-gray-800 border border-gray-200/80 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200 px-4 py-2 text-base sm:text-lg font-medium"
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: BRAND_BLUE }}
                      />
                      {label}
                  </span>
                  ))}
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-brand/75 via-brand-green/75 to-brand-yellow/75">
        <div className="mx-auto py-10 sm:py-12 lg:py-16 xl:py-20 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 lg:flex lg:items-center lg:justify-between" style={{ maxWidth: 'min(1600px, 94vw)' }}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
            <span className="block">Ready to modernize your client communications?</span>
            <span className="block opacity-90">Get started today.</span>
          </h2>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row lg:mt-0 lg:shrink-0 gap-3 sm:gap-0">
            <div className="inline-flex">
              <Link
                href="/get-started"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base sm:text-lg font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all duration-200 hover:shadow-md hover:scale-[1.01] hover:-translate-y-px w-full sm:w-auto"
              >
                Get started
              </Link>
            </div>
            <div className="sm:ml-3 inline-flex">
              <Link
                href="/request-demo"
                className="inline-flex items-center justify-center px-5 py-3 border border-white/30 text-base sm:text-lg font-medium rounded-full text-white/90 bg-transparent hover:bg-white/10 transition-all duration-200 hover:shadow-md hover:scale-[1.01] hover:-translate-y-px w-full sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 